import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import PageContainer from '../layout/PageContainer';
import PageHeader, { Accent } from '../layout/PageHeader';
import AlertConfigurationsList from './AlertConfigurationsList';
import AlertConfiguration from './AlertConfiguration';
import TradeBuilder from './TradeBuilder';
import { AlertConfiguration as AlertConfigType, AlertKind } from '../../types/dashboard';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

function kindFromAlert(a: AlertConfigType | null): AlertKind {
  if (!a) return 'directional_multi';
  const k = a.alert_kind;
  if (k === 'bidirectional_multi') return 'bidirectional_multi';
  if (k === 'directional_multi') return 'directional_multi';
  if (k === 'single' || a.is_multi_trigger) return 'directional_multi';
  return 'directional_multi';
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alerts-tabpanel-${index}`}
      aria-labelledby={`alerts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AlertsPage: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [editingConfig, setEditingConfig] = useState<AlertConfigType | null>(null);
  const [createFlowKind, setCreateFlowKind] = useState<AlertKind>('directional_multi');
  const [refreshKey, setRefreshKey] = useState(0);
  // Stable identity for the open form. Bumped only when we intentionally start a
  // new create/edit session so a stay-on-form save (which assigns an id to a new
  // alert) does NOT remount the form and reset a multi-step builder's progress.
  const [formInstance, setFormInstance] = useState(0);

  const alertKindForForm = editingConfig ? kindFromAlert(editingConfig) : createFlowKind;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setEditingConfig(null);
    setFormInstance((n) => n + 1);
  };

  const handleCreateDirectional = () => {
    setCreateFlowKind('directional_multi');
    setEditingConfig(null);
    setFormInstance((n) => n + 1);
    setActiveTab(1);
  };

  const handleCreateBidirectional = () => {
    setCreateFlowKind('bidirectional_multi');
    setEditingConfig(null);
    setFormInstance((n) => n + 1);
    setActiveTab(1);
  };

  const handleEdit = (config: AlertConfigType) => {
    setEditingConfig(config);
    setFormInstance((n) => n + 1);
    setActiveTab(1);
  };

  useEffect(() => {
    const state = location.state as { editAlertId?: number } | null | undefined;
    const editId = state?.editAlertId;
    if (!editId || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getAlertConfigurations(token);
        if (cancelled || !res.success || !Array.isArray(res.data)) return;
        const found = res.data.find((c) => String(c.id) === String(editId));
        if (found) {
          handleEdit(found as AlertConfigType);
          window.history.replaceState({}, document.title);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per navigation state
  }, [location.state, token]);

  const handleSave = (
    configData: Omit<AlertConfigType, 'id'> & { id?: string },
    options?: { stayOnForm?: boolean }
  ) => {
    console.log('Alert configuration saved:', configData, options);
    setRefreshKey((prev) => prev + 1);
    if (options?.stayOnForm && configData.id != null && String(configData.id) !== '') {
      const c = configData as AlertConfigType & {
        id: string;
        stockSymbol?: string;
        positionType?: string;
        secretWord?: string;
        webhookUrl?: string;
        isActive?: boolean;
        createdAt?: string;
        alertKind?: AlertKind;
        isMultiTrigger?: boolean;
        triggerCount?: number;
        slotSources?: string[];
      };
      setEditingConfig({
        id: String(c.id),
        user_id: c.user_id ?? 1,
        tenant_id: c.tenant_id ?? 'default',
        stock_symbol: c.stock_symbol ?? c.stockSymbol ?? editingConfig?.stock_symbol ?? '',
        position_type: c.position_type ?? (c.positionType as any) ?? editingConfig?.position_type ?? '',
        secret_word: c.secret_word ?? c.secretWord ?? editingConfig?.secret_word ?? '',
        webhook_url: c.webhook_url ?? c.webhookUrl ?? editingConfig?.webhook_url ?? '',
        is_active: c.is_active ?? c.isActive ?? editingConfig?.is_active ?? true,
        description: c.description ?? '',
        created_at: c.created_at ?? c.createdAt ?? editingConfig?.created_at ?? '',
        alert_kind: (c.alert_kind ?? c.alertKind ?? editingConfig?.alert_kind) as AlertKind | undefined,
        is_multi_trigger: c.is_multi_trigger ?? c.isMultiTrigger ?? editingConfig?.is_multi_trigger ?? false,
        trigger_count: c.trigger_count ?? c.triggerCount ?? editingConfig?.trigger_count,
        slot_sources: c.slot_sources ?? c.slotSources ?? editingConfig?.slot_sources ?? [],
        slot_descriptions:
          c.slot_descriptions ??
          (c as AlertConfigType & { slotDescriptions?: string[] }).slotDescriptions ??
          editingConfig?.slot_descriptions ??
          [],
        linked_directional_alert_id:
          c.linked_directional_alert_id ?? editingConfig?.linked_directional_alert_id ?? null,
        linked_directional_slot_index:
          c.linked_directional_slot_index ?? editingConfig?.linked_directional_slot_index ?? null,
      });
    } else if (!options?.stayOnForm) {
      setActiveTab(0);
      setEditingConfig(null);
    }
  };

  const handleCancel = () => {
    setEditingConfig(null);
    setActiveTab(0);
  };

  const handleTestWebhook = async (webhookUrl: string) => {
    try {
      const response = await apiService.testWebhook(webhookUrl, token || undefined);
      
      if (response.success) {
        // testWebhook returns { success: boolean; message: string } without execution_time_ms
        // Use a default execution time since it's not available in the response
        return { success: true, executionTime: 1.2 };
      } else {
        throw new Error(response.error || 'Webhook test failed');
      }
    } catch (error: any) {
      console.error('Webhook test failed:', error);
      return { success: false, executionTime: 0 };
    }
  };

  return (
    <PageContainer maxWidth={1440}>
      <PageHeader
        title={<>Your <Accent>alerts</Accent></>}
        subtitle="Create and manage the alerts that turn your indicator signals into trades. Each alert watches for aligned indicators and fires the matching webhook."
      />

      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="alerts tabs"
          variant="fullWidth"
        >
          <Tab label="My Alert Configurations" />
          <Tab
            label={
              editingConfig
                ? 'Edit Alert'
                : createFlowKind === 'directional_multi'
                  ? 'Create Directional Trade'
                  : 'Create Two-Way Trade'
            }
          />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <AlertConfigurationsList
          key={refreshKey}
          onEdit={handleEdit}
          onCreateDirectional={handleCreateDirectional}
          onCreateBidirectional={handleCreateBidirectional}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {alertKindForForm === 'bidirectional_multi' || alertKindForForm === 'directional_multi' ? (
          <TradeBuilder
            key={`form-${formInstance}`}
            alertKind={alertKindForForm}
            alert={editingConfig || undefined}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          <AlertConfiguration
            key={`form-${formInstance}`}
            alertKind={alertKindForForm}
            alert={editingConfig || undefined}
            onSave={handleSave}
            onCancel={handleCancel}
            onTestWebhook={handleTestWebhook}
          />
        )}
      </TabPanel>
    </PageContainer>
  );
};

export default AlertsPage;

